import * as Monitaro from '@/infrastructure/network/Monitaro/schema/models'
import { APIRequest } from '@/infrastructure/network/APIRequest'
import { HTTPMethod } from '@/infrastructure/network/types'

/**
 * ログイン
 *
 * Monitaro for hospitalsのログイン機能
 */
export class PostLogin implements APIRequest<Monitaro.LoginResponse> {
  response: Monitaro.LoginResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.POST
  params: Monitaro.LoginRequest
  constructor(params: Monitaro.LoginRequest) {
    this.params = params
    this.path = `/login`
  }
}

/**
 * リフレッシュ
 *
 * Monitaro for hospitalのリフレッシュ機能
ログインが失敗したときに2週間以内にコールすると新しいトークンが返ってくる
 */
export class GetRefresh implements APIRequest<Monitaro.RefreshResponse> {
  response: Monitaro.RefreshResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.GET
  constructor() {
    this.path = `/refresh`
  }
}

/**
 * メールアドレスの変更
 *
 * メールアドレスの変更依頼で送られてきたidとcodeを使ってメールアドレスの変更を行う。
 */
export class PutChangeEmail implements APIRequest<Monitaro.ChangeEmailResponse> {
  response: Monitaro.ChangeEmailResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.PUT
  params: Monitaro.ChangeEmailRequest
  constructor(id: string, params: Monitaro.ChangeEmailRequest) {
    this.params = params
    this.path = `/change/email/${id}`
  }
}

/**
 * パスワードの変更依頼
 *
 * ログインしていない状態でパスワードの変更依頼メールを送信する。
 */
export class PostForget implements APIRequest<Monitaro.ForgetResponse> {
  response: Monitaro.ForgetResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.POST
  params: Monitaro.ForgetRequest
  constructor(params: Monitaro.ForgetRequest) {
    this.params = params
    this.path = `/forget`
  }
}

/**
 * パスワードの変更
 *
 * パスワード変更依頼で送られてきたidとcodeを使ってパスワードの変更を行う。
 */
export class PutChangePassword implements APIRequest<Monitaro.ChangePasswordResponse> {
  response: Monitaro.ChangePasswordResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.PUT
  params: Monitaro.ChangePasswordRequest
  constructor(id: string, params: Monitaro.ChangePasswordRequest) {
    this.params = params
    this.path = `/change/password/${id}`
  }
}

/**
 * 病院管理者ユーザーのアクティベーション
 *
 * idとcodeを指定して、ユーザー名とパスワードの設定が行える。
 */
export class PostActivate implements APIRequest<Monitaro.ActivateResponse> {
  response: Monitaro.ActivateResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.POST
  params: Monitaro.ActivateRequest
  constructor(id: string, params: Monitaro.ActivateRequest) {
    this.params = params
    this.path = `/activate/${id}`
  }
}

/**
 * ログアウト
 *
 * Monitaro for hospitalのログアウト機能
ログインしていない状態でコールするとエラーを返す
 */
export class PostLogout implements APIRequest<Monitaro.LogoutResponse> {
  response: Monitaro.LogoutResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.POST
  constructor() {
    this.path = `/logout`
  }
}

/**
 * ログインユーザー情報
 *
 * ログインしているユーザーのユーザー情報を取得する
 */
export class GetMe implements APIRequest<Monitaro.MeResponse> {
  response: Monitaro.MeResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.GET
  constructor() {
    this.path = `/me`
  }
}

/**
 * ログインユーザー情報の更新
 *
 * ログインしているユーザーのユーザー情報を更新する
usernameとpasswordは変更することができない
 */
export class PutProfile implements APIRequest<Monitaro.ProfileResponse> {
  response: Monitaro.ProfileResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.PUT
  params: Monitaro.ProfileRequest
  constructor(params: Monitaro.ProfileRequest) {
    this.params = params
    this.path = `/profile`
  }
}

/**
 * カレンダー表示設定の更新
 *
 * ログインしているユーザーのカレンダー表示設定（is_calendar_visible）を更新する
 * PutProfile と同じエンドポイント（PUT /profile）のため実装を継承し、差分の発生を防ぐ
 */
export class PutCalendarVisible extends PutProfile {}

/**
 * カレンダーブロック設定の更新
 *
 * ログインしているユーザーのカレンダーブロック設定（is_calendar_blocking）を更新する
 * PutProfile と同じエンドポイント（PUT /profile）のため実装を継承し、差分の発生を防ぐ
 */
export class PutCalendarBlocking extends PutProfile {}

/**
 * FCMトークンの更新
 *
 * ログインしているユーザーのFCMトークンを更新する
 */
export class PutFcmToken implements APIRequest<Monitaro.TokenResponse> {
  response: Monitaro.TokenResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.PUT
  params: Monitaro.TokenRequest
  constructor(params: Monitaro.TokenRequest) {
    this.params = params
    this.path = `/profile/fcm`
  }
}

/**
 * メールアドレスの変更依頼
 *
 * ログイン状態でメールドレスの変更通知を行う。
APIをコールすると変更用のメールが送信される。
 */
export class PostEmail implements APIRequest<Monitaro.EmailResponse> {
  response: Monitaro.EmailResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.POST
  params: Monitaro.EmailRequest
  constructor(params: Monitaro.EmailRequest) {
    this.params = params
    this.path = `/email`
  }
}

/**
 * パスワードの変更
 *
 * ログイン状態でパスワードの変更を行う。
 */
export class PostPassword implements APIRequest<Monitaro.PasswordResponse> {
  response: Monitaro.PasswordResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.POST
  params: Monitaro.PasswordRequest
  constructor(params: Monitaro.PasswordRequest) {
    this.params = params
    this.path = `/password`
  }
}
